// src/pages/workloads/ManageWorkloadsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  List,
  Grid3X3,
  Map,
  Filter,
  Search,
  MoreVertical,
  Trash2,
  Play,
  Pause,
  Archive,
  Users,
  RefreshCw,
  Download,
  ChevronDown,
  Check,
  Loader2,
  X,
  UserPlus,
  FileText,
  Folder,
  Sparkles,
} from 'lucide-react';
import { useWorkloadStore, useFilteredWorkloads } from '../../store/workloadStore';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { workloadService } from '../../services/workload.service';
import { surveyService } from '../../services/survey.service';
import { projectService } from '../../services/project.service';
import { getUsers } from '../../services/user.service';
import { Workload, WorkloadStatus } from '../../types/workload.types';
import { UserProfile } from '../../types/user.types';
import { Survey } from '../../types/survey.types';
import { DocumentSnapshot } from 'firebase/firestore';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap from '../../components/map/BaseMap';
import MarkerManager, { MarkerData, StatusLegend } from '../../components/map/MarkerManager';
import { Marker, InfoWindow } from '@react-google-maps/api';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: WorkloadStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'paused', label: 'Paused', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-600' },
];

// Workload status colors for map markers
const WORKLOAD_STATUS_COLORS: Record<WorkloadStatus, string> = {
  draft: '#9CA3AF',     // Gray
  active: '#22C55E',    // Green
  paused: '#F59E0B',    // Yellow
  completed: '#3B82F6', // Blue
  archived: '#6B7280',  // Dark Gray
};

// Workload Map View Component
const WorkloadMapView: React.FC<{
  workloads: Workload[];
  onWorkloadClick: (workload: Workload) => void;
}> = ({ workloads, onWorkloadClick }) => {
  const [selectedWorkload, setSelectedWorkload] = useState<Workload | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // Calculate center from workload - try multiple sources
  const getWorkloadCenter = (workload: Workload): { lat: number; lng: number } | null => {
    try {
      // Try centerPoint first
      if (workload.territory?.centerPoint) {
        const { lat, lng } = workload.territory.centerPoint;
        // Validate coordinates are reasonable
        if (typeof lat === 'number' && typeof lng === 'number' &&
            lat !== 0 && lng !== 0 &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return { lat, lng };
        }
      }

      // Try bounds center
      if (workload.territory?.bounds) {
        const { north, south, east, west } = workload.territory.bounds;
        // Validate bounds are reasonable
        if (typeof north === 'number' && typeof south === 'number' &&
            typeof east === 'number' && typeof west === 'number' &&
            north !== 0 && south !== 0 && east !== 0 && west !== 0) {
          const lat = (north + south) / 2;
          const lng = (east + west) / 2;
          if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { lat, lng };
          }
        }
      }

      // Try polygon coordinates
      if (workload.territory?.polygon?.coordinates && Array.isArray(workload.territory.polygon.coordinates) && workload.territory.polygon.coordinates.length > 0) {
        const coords = workload.territory.polygon.coordinates;
        const avgLat = coords.reduce((sum, c) => sum + (c?.lat || 0), 0) / coords.length;
        const avgLng = coords.reduce((sum, c) => sum + (c?.lng || 0), 0) / coords.length;
        if (avgLat !== 0 && avgLng !== 0 && avgLat >= -90 && avgLat <= 90 && avgLng >= -180 && avgLng <= 180) {
          return { lat: avgLat, lng: avgLng };
        }
      }

      // Try sourceMetadata for saturation workloads
      const meta = workload.sourceMetadata as any;
      if (meta?.pointLocation || meta?.originPoint) {
        const point = meta.pointLocation || meta.originPoint;
        if (point?.lat && point?.lng && typeof point.lat === 'number' && typeof point.lng === 'number') {
          return { lat: point.lat, lng: point.lng };
        }
      }

      return null;
    } catch (error) {
      console.warn('Error extracting workload center:', workload.id, error);
      return null;
    }
  };

  // Get all workloads with valid centers
  const workloadsWithCenters = workloads
    .map(w => ({ workload: w, center: getWorkloadCenter(w) }))
    .filter((item): item is { workload: Workload; center: { lat: number; lng: number } } =>
      item.center !== null
    );

  // Calculate map center and zoom to fit all workloads
  const mapCenter = workloadsWithCenters.length > 0
    ? {
        lat: workloadsWithCenters.reduce((sum, w) => sum + w.center.lat, 0) / workloadsWithCenters.length,
        lng: workloadsWithCenters.reduce((sum, w) => sum + w.center.lng, 0) / workloadsWithCenters.length,
      }
    : { lat: 39.8283, lng: -98.5795 }; // US center as fallback

  // Fit bounds when map loads
  useEffect(() => {
    if (mapInstance && workloadsWithCenters.length > 0 && typeof google !== 'undefined' && google.maps?.LatLngBounds) {
      const bounds = new google.maps.LatLngBounds();
      workloadsWithCenters.forEach(({ center }) => {
        bounds.extend(center);
      });
      mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [mapInstance, workloadsWithCenters.length]);

  const getMarkerIcon = (status: WorkloadStatus): google.maps.Symbol | undefined => {
    if (typeof google === 'undefined' || !google.maps?.SymbolPath) return undefined;
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: WORKLOAD_STATUS_COLORS[status],
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 12,
    };
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="relative h-[600px]">
        <GoogleMapProvider>
          <BaseMap
            center={mapCenter}
            zoom={workloadsWithCenters.length > 0 ? 10 : 4}
            height="100%"
            onMapLoad={setMapInstance}
          >
            {workloadsWithCenters.map(({ workload, center }) => (
              <Marker
                key={workload.id}
                position={center}
                title={workload.name}
                icon={getMarkerIcon(workload.status)}
                onClick={() => setSelectedWorkload(workload)}
              />
            ))}

            {selectedWorkload && getWorkloadCenter(selectedWorkload) && (
              <InfoWindow
                position={getWorkloadCenter(selectedWorkload)!}
                onCloseClick={() => setSelectedWorkload(null)}
              >
                <div className="min-w-[220px] p-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{selectedWorkload.name}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center justify-between">
                      <span>Status:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        STATUS_OPTIONS.find(s => s.value === selectedWorkload.status)?.color
                      }`}>
                        {STATUS_OPTIONS.find(s => s.value === selectedWorkload.status)?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Addresses:</span>
                      <span className="font-medium">{selectedWorkload.progress?.totalAddresses || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Completion:</span>
                      <span className="font-medium">{selectedWorkload.progress?.completionPercentage || 0}%</span>
                    </div>
                    {selectedWorkload.assignedToNames?.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span>Assigned:</span>
                        <span className="font-medium text-xs">{selectedWorkload.assignedToNames.slice(0, 2).join(', ')}{selectedWorkload.assignedToNames.length > 2 ? '...' : ''}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onWorkloadClick(selectedWorkload)}
                    className="mt-3 w-full py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </InfoWindow>
            )}
          </BaseMap>
        </GoogleMapProvider>

        {/* Workload Status Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Workload Status</h4>
          <div className="space-y-1">
            {STATUS_OPTIONS.map(({ value, label }) => (
              <div key={value} className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-2 border border-white"
                  style={{ backgroundColor: WORKLOAD_STATUS_COLORS[value] }}
                />
                <span className="text-xs text-gray-600">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* No workloads with location message */}
        {workloadsWithCenters.length === 0 && workloads.length > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <div className="text-center p-6">
              <Map className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">
                {workloads.length} workload{workloads.length !== 1 ? 's' : ''} found, but none have valid location data.
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Try switching to List or Grid view to see your workloads.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ManageWorkloadsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const {
    list,
    setWorkloads,
    setListLoading,
    setFilters,
    clearFilters,
    setViewMode,
    selectWorkload,
    deselectWorkload,
    selectAllWorkloads,
    clearWorkloadSelection,
  } = useWorkloadStore();

  const filteredWorkloads = useFilteredWorkloads();

  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBatchMenu, setShowBatchMenu] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Use global project context
  const { projects, selectedProjectId, selectedProject } = useProject();

  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentWorkloadId, setAssignmentWorkloadId] = useState<string | null>(null);
  const [assignmentWorkload, setAssignmentWorkload] = useState<Workload | null>(null);
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [originalAssignedIds, setOriginalAssignedIds] = useState<string[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Survey change modal state
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyWorkloadId, setSurveyWorkloadId] = useState<string | null>(null);
  const [availableSurveys, setAvailableSurveys] = useState<Survey[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [isChangingSurvey, setIsChangingSurvey] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteWorkloadIds, setDeleteWorkloadIds] = useState<string[]>([]);
  const [deletionPreview, setDeletionPreview] = useState<{
    workloads: Array<{ id: string; name: string; addressCount: number; responseCount: number }>;
    totalAddresses: number;
    totalResponses: number;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Clear filters when user changes to prevent stale filters from affecting new user
  useEffect(() => {
    // Clear any persisted filters that might not apply to the current user
    clearFilters();
  }, [currentUser?.uid, clearFilters]);

  // Load workloads for selected project (or user's assigned workloads for field users)
  const loadWorkloads = useCallback(async (loadMore = false) => {
    if (!currentUser) {
      setListLoading(false);
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setListLoading(true);
      setLastDoc(null);
    }

    try {
      let workloads: Workload[];
      let newLastDoc: DocumentSnapshot | null = null;

      // Field users only see workloads assigned to them (no pagination needed - typically small list)
      if (userProfile?.role === 'field') {
        workloads = await workloadService.getUserWorkloads(currentUser.uid);
        setHasMore(false);
      } else if (selectedProjectId) {
        // Admin/client users see all workloads for the project with pagination
        const result = await workloadService.getPaginated(
          selectedProjectId,
          PAGE_SIZE,
          loadMore && lastDoc ? lastDoc : undefined
        );
        workloads = result.workloads;
        newLastDoc = result.lastDoc;
        setHasMore(result.workloads.length === PAGE_SIZE);
      } else {
        workloads = [];
        setHasMore(false);
      }

      setLastDoc(newLastDoc);

      if (loadMore) {
        setWorkloads([...list.workloads, ...workloads]);
      } else {
        setWorkloads(workloads);
      }
    } catch (error) {
      console.error('Error loading workloads:', error);
      toast.error('Failed to load workloads');
    } finally {
      setListLoading(false);
      setLoadingMore(false);
    }
  }, [currentUser, selectedProjectId, userProfile?.role, setWorkloads, setListLoading, lastDoc, list.workloads]);

  useEffect(() => {
    loadWorkloads();
  }, [currentUser, selectedProjectId, userProfile?.role]);

  // Helper to reload workloads based on user role (resets to first page)
  const reloadWorkloads = async () => {
    setLastDoc(null);
    await loadWorkloads(false);
  };

  // Handle search
  useEffect(() => {
    setFilters({ search: searchTerm });
  }, [searchTerm, setFilters]);

  // Batch actions (excluding delete - that uses the modal)
  const handleBatchAction = useCallback(async (action: 'activate' | 'pause' | 'archive') => {
    if (list.selectedWorkloadIds.length === 0) return;

    const confirmMessage = `${action.charAt(0).toUpperCase() + action.slice(1)} ${list.selectedWorkloadIds.length} workloads?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      let status: WorkloadStatus | undefined;
      if (action === 'activate') status = 'active';
      if (action === 'pause') status = 'paused';
      if (action === 'archive') status = 'archived';

      if (status) {
        await workloadService.batchUpdateStatus(
          list.selectedWorkloadIds,
          status,
          currentUser!.uid
        );
        toast.success(`${list.selectedWorkloadIds.length} workloads updated`);
      }

      // Refresh list
      await reloadWorkloads();
      clearWorkloadSelection();
    } catch (error) {
      console.error('Batch action error:', error);
      toast.error('Failed to perform action');
    } finally {
      setShowBatchMenu(false);
    }
  }, [list.selectedWorkloadIds, currentUser, selectedProjectId, setWorkloads, clearWorkloadSelection]);

  // Open delete confirmation modal with preview
  const openDeleteModal = async (workloadIds: string[]) => {
    setDeleteWorkloadIds(workloadIds);
    setDeleteConfirmText('');
    setShowBatchMenu(false);
    setShowDeleteModal(true);
    setIsLoadingPreview(true);

    try {
      // Load deletion preview for each workload
      const previews = await Promise.all(
        workloadIds.map(async (id) => {
          const preview = await workloadService.getDeletionPreview(id);
          return preview ? { id, ...preview } : null;
        })
      );

      const validPreviews = previews.filter((p): p is NonNullable<typeof p> => p !== null);
      const totalAddresses = validPreviews.reduce((sum, p) => sum + p.addressCount, 0);
      const totalResponses = validPreviews.reduce((sum, p) => sum + p.responseCount, 0);

      setDeletionPreview({
        workloads: validPreviews.map(p => ({
          id: p.id,
          name: p.workloadName,
          addressCount: p.addressCount,
          responseCount: p.responseCount,
        })),
        totalAddresses,
        totalResponses,
      });
    } catch (error) {
      console.error('Error loading deletion preview:', error);
      toast.error('Failed to load deletion preview');
      setShowDeleteModal(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Handle cascade delete
  const handleCascadeDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      let totalAddressesDeleted = 0;
      let totalResponsesDeleted = 0;

      for (const workloadId of deleteWorkloadIds) {
        const result = await workloadService.cascadeDelete(workloadId, currentUser!.uid);
        totalAddressesDeleted += result.addressesDeleted;
        totalResponsesDeleted += result.responsesDeleted;
      }

      toast.success(
        `Deleted ${deleteWorkloadIds.length} workload(s), ${totalAddressesDeleted} addresses, and ${totalResponsesDeleted} survey responses`
      );

      // Refresh list and close modal
      await reloadWorkloads();
      clearWorkloadSelection();
      setShowDeleteModal(false);
      setDeletionPreview(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Cascade delete error:', error);
      toast.error('Failed to delete workloads');
    } finally {
      setIsDeleting(false);
    }
  };

  // Open assignment modal
  const openAssignModal = async (workloadId?: string, workload?: Workload) => {
    setAssignmentWorkloadId(workloadId || null);
    setAssignmentWorkload(workload || null);
    setShowBatchMenu(false);
    setUserSearchTerm('');

    // Pre-select current assignments if single workload
    const currentAssigned = workload?.assignedTo || [];
    setSelectedUserIds([...currentAssigned]);
    setOriginalAssignedIds([...currentAssigned]);

    setShowAssignModal(true);

    // Load available users
    try {
      const users = await getUsers();
      // Filter to field users and users in the same org
      const fieldUsers = users.filter(u =>
        u.role === 'field' || u.role === 'client_user'
      ) as UserProfile[];
      setAvailableUsers(fieldUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  // Handle assignment changes (add and remove users)
  const handleAssignUsers = async () => {
    setIsAssigning(true);
    try {
      const workloadIds = assignmentWorkloadId
        ? [assignmentWorkloadId]
        : list.selectedWorkloadIds;

      // Calculate who was added and who was removed
      const addedUserIds = selectedUserIds.filter(id => !originalAssignedIds.includes(id));
      const removedUserIds = originalAssignedIds.filter(id => !selectedUserIds.includes(id));

      // Get user details for logging
      const addedUsers = availableUsers.filter(u => addedUserIds.includes(u.uid));
      const addedNames = addedUsers.map(u => u.name || `${u.firstName} ${u.lastName}`);
      const removedUsers = availableUsers.filter(u => removedUserIds.includes(u.uid));
      const removedNames = removedUsers.map(u => u.name || `${u.firstName} ${u.lastName}`);

      const performerName = userProfile?.name || `${userProfile?.firstName} ${userProfile?.lastName}` || 'Admin';

      for (const workloadId of workloadIds) {
        // Unassign removed users first
        if (removedUserIds.length > 0) {
          await workloadService.unassignUsers(
            workloadId,
            removedUserIds,
            currentUser!.uid,
            removedNames,
            performerName
          );
        }

        // Assign new users
        if (addedUserIds.length > 0) {
          await workloadService.assignToUsers(
            workloadId,
            addedUserIds,
            addedNames,
            currentUser!.uid,
            performerName
          );
        }
      }

      // Create success message
      const messages: string[] = [];
      if (addedUserIds.length > 0) {
        messages.push(`assigned ${addedUserIds.length} user(s)`);
      }
      if (removedUserIds.length > 0) {
        messages.push(`removed ${removedUserIds.length} user(s)`);
      }

      if (messages.length > 0) {
        toast.success(`Successfully ${messages.join(' and ')} for ${workloadIds.length} workload(s)`);
      } else {
        toast.info('No changes were made');
      }

      // Refresh list
      await reloadWorkloads();

      setShowAssignModal(false);
      clearWorkloadSelection();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error('Failed to update assignments');
    } finally {
      setIsAssigning(false);
    }
  };

  // Open survey change modal
  const openSurveyModal = async (workloadId?: string) => {
    setSurveyWorkloadId(workloadId || null);
    setSelectedSurveyId('');

    // Load available surveys
    try {
      let surveys: Survey[] = [];
      if (selectedProjectId) {
        surveys = await surveyService.getActiveSurveys(selectedProjectId);
      }
      if (surveys.length === 0) {
        surveys = await surveyService.getAllActiveSurveys();
      }
      setAvailableSurveys(surveys);
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast.error('Failed to load surveys');
    }

    setShowSurveyModal(true);
  };

  // Handle survey change
  const handleChangeSurvey = async () => {
    // Allow "__none__" for removing survey, but require selection otherwise
    if (!selectedSurveyId) {
      toast.error('Please select an option');
      return;
    }

    setIsChangingSurvey(true);
    try {
      const workloadIds = surveyWorkloadId
        ? [surveyWorkloadId]
        : list.selectedWorkloadIds;

      if (selectedSurveyId === '__none__') {
        // Unlink survey (lead generation mode)
        for (const workloadId of workloadIds) {
          await workloadService.unlinkSurvey(workloadId, currentUser!.uid);
        }
        toast.success(`Survey removed from ${workloadIds.length} workload(s). Lead generation mode enabled for lead projects.`);
      } else {
        // Link survey
        const selectedSurvey = availableSurveys.find(s => s.id === selectedSurveyId);
        const surveyName = selectedSurvey?.name || 'Unknown Survey';

        for (const workloadId of workloadIds) {
          await workloadService.linkSurvey(
            workloadId,
            selectedSurveyId,
            surveyName,
            currentUser!.uid
          );
        }
        toast.success(`Survey "${surveyName}" linked to ${workloadIds.length} workload(s)`);
      }

      // Refresh list
      await reloadWorkloads();

      setShowSurveyModal(false);
      clearWorkloadSelection();
    } catch (error) {
      console.error('Survey change error:', error);
      toast.error('Failed to change survey');
    } finally {
      setIsChangingSurvey(false);
    }
  };

  // Render workload card
  const renderWorkloadCard = (workload: Workload) => {
    const statusOption = STATUS_OPTIONS.find((s) => s.value === workload.status);
    const isSelected = list.selectedWorkloadIds.includes(workload.id);

    return (
      <div
        key={workload.id}
        className={`bg-white rounded-lg border ${
          isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'
        } p-4 hover:shadow-md transition-shadow cursor-pointer`}
        onClick={() => navigate(`/workloads/${workload.id}`)}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                isSelected ? deselectWorkload(workload.id) : selectWorkload(workload.id);
              }}
              className="h-4 w-4 text-indigo-600 rounded border-gray-300 mr-3"
            />
            <div>
              <h3 className="font-medium text-gray-900">{workload.name}</h3>
              <p className="text-sm text-gray-500">
                {workload.type === 'saturation' ? 'Saturation' :
                 workload.type === 'csv_upload' ? 'CSV' : 'Census'}
              </p>
            </div>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusOption?.color}`}>
            {statusOption?.label}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{workload.progress.completionPercentage}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${workload.progress.completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <p className="text-gray-400">Total</p>
            <p className="font-semibold text-gray-700">{workload.progress.totalAddresses}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400">Completed</p>
            <p className="font-semibold text-green-600">{workload.progress.completed}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-400">Pending</p>
            <p className="font-semibold text-gray-600">{workload.progress.pending}</p>
          </div>
        </div>

        {/* Assigned users and assign button */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-gray-500">
              <Users className="h-3 w-3 mr-1" />
              {workload.assignedToNames && workload.assignedToNames.length > 0 ? (
                <>
                  {workload.assignedToNames.slice(0, 2).join(', ')}
                  {workload.assignedToNames.length > 2 && (
                    <span className="ml-1">+{workload.assignedToNames.length - 2}</span>
                  )}
                </>
              ) : (
                <span className="text-gray-400">Unassigned</span>
              )}
            </div>
            {userProfile?.role !== 'field' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openAssignModal(workload.id, workload);
                }}
                className="flex items-center text-xs px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded"
                title="Manage assignments"
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Assign
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Workloads</h1>
          <p className="text-gray-600 mt-1">
            {filteredWorkloads.length} workload{filteredWorkloads.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Project indicator */}
          {selectedProject && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
              <Folder className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">{selectedProject.name}</span>
            </div>
          )}
          {/* Create button - only for non-field users */}
          {userProfile?.role !== 'field' && (
            <button
              onClick={() => navigate('/workloads/create')}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Workload
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search workloads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* View mode */}
          <div className="flex items-center border border-gray-300 rounded-md">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${list.viewMode === 'list' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500'}`}
              title="List view"
            >
              <List className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${list.viewMode === 'grid' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500'}`}
              title="Grid view"
            >
              <Grid3X3 className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 ${list.viewMode === 'map' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500'}`}
              title="Map view"
            >
              <Map className="h-5 w-5" />
            </button>
          </div>

          {/* Filter button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-3 py-2 border rounded-md ${
              showFilters ? 'border-indigo-500 text-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </button>

          {/* Batch actions */}
          {list.selectedWorkloadIds.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowBatchMenu(!showBatchMenu)}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700"
              >
                <span>{list.selectedWorkloadIds.length} selected</span>
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>

              {showBatchMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleBatchAction('activate')}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Activate
                  </button>
                  <button
                    onClick={() => handleBatchAction('pause')}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </button>
                  <button
                    onClick={() => handleBatchAction('archive')}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => openAssignModal()}
                    className="w-full flex items-center px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign Users
                  </button>
                  <button
                    onClick={() => openSurveyModal()}
                    className="w-full flex items-center px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Change Survey
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => openDeleteModal(list.selectedWorkloadIds)}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Status</label>
                <select
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters({ status: value ? [value as WorkloadStatus] : undefined });
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={clearFilters}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {list.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredWorkloads.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Map className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No workloads found</h3>
          <p className="text-gray-500 mt-2">
            {list.workloads.length === 0
              ? userProfile?.role === 'field'
                ? 'No workloads have been assigned to you yet.'
                : 'Create your first workload to get started.'
              : 'Try adjusting your filters.'}
          </p>
          {list.workloads.length === 0 && userProfile?.role !== 'field' && (
            <button
              onClick={() => navigate('/workloads/create')}
              className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Workload
            </button>
          )}
        </div>
      ) : list.viewMode === 'map' ? (
        <WorkloadMapView
          workloads={filteredWorkloads}
          onWorkloadClick={(workload) => navigate(`/workloads/${workload.id}`)}
        />
      ) : (
        <>
          <div className={`grid gap-4 ${
            list.viewMode === 'grid' ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'
          }`}>
            {filteredWorkloads.map(renderWorkloadCard)}
          </div>

          {/* Load More Button */}
          {hasMore && !list.isLoading && (
            <div className="flex justify-center py-6">
              <button
                onClick={() => loadWorkloads(true)}
                disabled={loadingMore}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>Load More ({list.workloads.length} loaded)</>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {assignmentWorkload ? 'Manage Assignments' : 'Assign Users'}
                </h2>
                {assignmentWorkload && (
                  <p className="text-sm text-gray-500">{assignmentWorkload.name}</p>
                )}
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Changes summary */}
              {(selectedUserIds.filter(id => !originalAssignedIds.includes(id)).length > 0 ||
                originalAssignedIds.filter(id => !selectedUserIds.includes(id)).length > 0) && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
                  <p className="font-medium text-amber-800">Pending changes:</p>
                  <ul className="mt-1 text-amber-700">
                    {selectedUserIds.filter(id => !originalAssignedIds.includes(id)).length > 0 && (
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Adding {selectedUserIds.filter(id => !originalAssignedIds.includes(id)).length} user(s)
                      </li>
                    )}
                    {originalAssignedIds.filter(id => !selectedUserIds.includes(id)).length > 0 && (
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        Removing {originalAssignedIds.filter(id => !selectedUserIds.includes(id)).length} user(s)
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {availableUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No field users available</p>
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md">
                  {availableUsers
                    .filter(user => {
                      if (!userSearchTerm) return true;
                      const search = userSearchTerm.toLowerCase();
                      const name = user.name || `${user.firstName} ${user.lastName}`;
                      return name.toLowerCase().includes(search) || user.email.toLowerCase().includes(search);
                    })
                    .map((user) => {
                      const isSelected = selectedUserIds.includes(user.uid);
                      const wasOriginallyAssigned = originalAssignedIds.includes(user.uid);
                      const isBeingAdded = isSelected && !wasOriginallyAssigned;
                      const isBeingRemoved = !isSelected && wasOriginallyAssigned;
                      const userName = user.name || `${user.firstName} ${user.lastName}`;

                      return (
                        <label
                          key={user.uid}
                          className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                            isBeingAdded ? 'bg-green-50' : isBeingRemoved ? 'bg-red-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds([...selectedUserIds, user.uid]);
                              } else {
                                setSelectedUserIds(selectedUserIds.filter((id) => id !== user.uid));
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 rounded border-gray-300 mr-3"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 flex items-center">
                              {userName}
                              {wasOriginallyAssigned && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                                  Current
                                </span>
                              )}
                              {isBeingAdded && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                  Adding
                                </span>
                              )}
                              {isBeingRemoved && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                  Removing
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            {user.role === 'field' ? 'Field' : 'Client User'}
                          </span>
                        </label>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-500">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignUsers}
                  disabled={isAssigning}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAssigning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Survey Change Modal */}
      {showSurveyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Change Survey for Workload{list.selectedWorkloadIds.length > 1 ? 's' : ''}
              </h2>
              <button
                onClick={() => setShowSurveyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                {surveyWorkloadId
                  ? 'Select a survey to link to this workload:'
                  : `Select a survey to link to ${list.selectedWorkloadIds.length} selected workload(s):`}
              </p>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {/* No Survey / Lead Generation Mode Option */}
                <label
                  className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 ${
                    selectedSurveyId === '__none__' ? 'bg-emerald-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="survey"
                    checked={selectedSurveyId === '__none__'}
                    onChange={() => setSelectedSurveyId('__none__')}
                    className="h-4 w-4 text-emerald-600 border-gray-300 mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 flex items-center">
                      <Sparkles className="h-4 w-4 mr-1.5 text-emerald-600" />
                      No Survey (Lead Generation Mode)
                    </p>
                    <p className="text-sm text-gray-500">
                      On completion, lead capture form opens for lead projects
                    </p>
                  </div>
                </label>

                {/* Available Surveys */}
                {availableSurveys.map((survey) => (
                  <label
                    key={survey.id}
                    className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      selectedSurveyId === survey.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="survey"
                      checked={selectedSurveyId === survey.id}
                      onChange={() => setSelectedSurveyId(survey.id)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{survey.name}</p>
                      {survey.description && (
                        <p className="text-sm text-gray-500 truncate">{survey.description}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      survey.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {survey.status}
                    </span>
                  </label>
                ))}

                {availableSurveys.length === 0 && (
                  <div className="text-center py-4 text-gray-500 border-t border-gray-100">
                    <p className="text-sm">No surveys available</p>
                    <button
                      onClick={() => {
                        setShowSurveyModal(false);
                        navigate('/surveys');
                      }}
                      className="mt-1 text-indigo-600 hover:text-indigo-700 text-sm"
                    >
                      Create a survey
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowSurveyModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeSurvey}
                disabled={!selectedSurveyId || isChangingSurvey}
                className={`flex items-center px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedSurveyId === '__none__'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isChangingSurvey ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : selectedSurveyId === '__none__' ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Enable Lead Mode
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Link Survey
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-red-900">
                    Permanently Delete Workload{deleteWorkloadIds.length > 1 ? 's' : ''}
                  </h2>
                  <p className="text-sm text-red-700">This action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletionPreview(null);
                  setDeleteConfirmText('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading deletion preview...</span>
                </div>
              ) : deletionPreview ? (
                <>
                  {/* Warning message */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800 font-medium mb-2">
                      You are about to permanently delete:
                    </p>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li className="flex items-center">
                        <span className="font-semibold mr-2">{deletionPreview.workloads.length}</span>
                        workload{deletionPreview.workloads.length !== 1 ? 's' : ''}
                      </li>
                      <li className="flex items-center">
                        <span className="font-semibold mr-2">{deletionPreview.totalAddresses}</span>
                        address{deletionPreview.totalAddresses !== 1 ? 'es' : ''}
                      </li>
                      <li className="flex items-center">
                        <span className="font-semibold mr-2">{deletionPreview.totalResponses}</span>
                        survey response{deletionPreview.totalResponses !== 1 ? 's' : ''}
                      </li>
                    </ul>
                  </div>

                  {/* Workload list */}
                  {deletionPreview.workloads.length <= 5 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Workloads to delete:</p>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                        {deletionPreview.workloads.map((workload) => (
                          <div key={workload.id} className="flex items-center justify-between p-2 border-b border-gray-100 last:border-b-0">
                            <span className="font-medium text-gray-900 text-sm">{workload.name}</span>
                            <span className="text-xs text-gray-500">
                              {workload.addressCount} addr, {workload.responseCount} resp
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirmation input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE here"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      autoFocus
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Error loading deletion preview</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletionPreview(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleCascadeDelete}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting || isLoadingPreview}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Permanently Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageWorkloadsPage;
